import { useCallback, useState } from 'react'

export interface UseMultiLoading {
  /**
   * denotes whether the supplied key is loading
   *
   * if no key is provided, denotes whether any key is loading
   *
   * @param key
   */
  loading: (key?: string) => boolean
  /**
   * sets the current key as loading
   *
   * @param key
   */
  addLoading: (key: string) => void
  /**
   * unsets the current key as loading
   *
   * @param key
   */
  removeLoading: (key: string) => void
}

/**
 * This hook lets us set and check the loading state for multiple items
 *
 * @see {@link UseMultiLoading}
 *
 * @example
 * const someMutation = async (key) => {
 *   addLoading(key)
 *   await mutation({ ... })
 *   removeLoading(key)
 * }
 * ...
 * loading={loading(key)}
 *
 * @deprecated use {@link useKeyedBooleans}
 * @expires 2024-01-01
 */
export function useMultiLoading(): UseMultiLoading {
  const [_loading, setLoading] = useState<string[]>([])
  const loading = useCallback<UseMultiLoading['loading']>(
    (key) => (key ? _loading.includes(key) : _loading.length > 0),
    [_loading]
  )
  const addLoading = useCallback<UseMultiLoading['addLoading']>(
    (key) => setLoading((prev) => [...prev, key]),
    []
  )
  const removeLoading = useCallback<UseMultiLoading['removeLoading']>(
    (key) => setLoading((prev) => prev.filter((p) => p !== key)),
    []
  )
  return {
    loading,
    addLoading,
    removeLoading,
  }
}
